import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  primaryRoles,
  seniorityLevels,
  languages,
  qaSkills,
  areasOfExpertise,
  methodologyNames,
} from "../../config";
import api from "../../api/axiosInstance";
import { getCandidateRole } from "../../components/utils/candidateRole";
import { parseBooleanQuery, evaluateBooleanQuery } from "../../components/utils/booleanQueryParser";
import { getCountryLabel } from "./candidateQuickView";
import CandidateQuickViewDialog from "./CandidateQuickViewDialog";

const SKILL_MIN_CHARS = 3;

// Restored Phase 1 behavior (companion to the level-based chip flow below):
// live as the user types, until a level is selected and turns the text into
// a chip. "react, node, aws" ->
// ["react", "node", "aws"], dropping anything under SKILL_MIN_CHARS so a
// short in-progress term never affects results.
const parseSkillTerms = (value) =>
  (value || "")
    .split(",")
    .map((term) => term.trim().toLowerCase())
    .filter((term) => term.length >= SKILL_MIN_CHARS);

// Phase 2 (see specs/expertise-search-skill-level-filtering.md): ordinal
// position maps directly onto the "1"-"5" level values stored on
// techSkills/qaSkill entries (index + 1 === stored level). Labels follow the
// CV's Area of Expertise / Methods scale.
const EXPERTISE_LEVEL_OPTIONS = [
  { value: "novice", label: "Novice" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];

// Combined techSkills + qaSkill + expertise + methods catalog used to
// validate a typed skill against the catalog's value slug,
// case-insensitively. A value repeated across the lists (e.g. "python" in
// languages and "Python" in areasOfExpertise) is kept once, first wins.
export const EXPERTISE_SKILL_CATALOG = [
  ...languages,
  ...qaSkills,
  ...areasOfExpertise.map(({ value, label }) => ({ value, name: label })),
  ...methodologyNames.map((name) => ({ value: name, name })),
].filter((skill, i, all) => all.findIndex((s) => s.value.toLowerCase() === skill.value.toLowerCase()) === i);

const findExpertiseSkill = (term) => {
  const normalized = (term || "").trim().toLowerCase();
  if (!normalized) return null;
  return EXPERTISE_SKILL_CATALOG.find((s) => s.value.toLowerCase() === normalized) || null;
};

const expertiseLevelOrdinal = (levelOptionValue) =>
  EXPERTISE_LEVEL_OPTIONS.findIndex((o) => o.value === levelOptionValue) + 1;

// A stored techSkills/qaSkill "level" is only trustworthy if it's an
// integer 1-5 (schema has no enum enforcing this - see Benny's
// investigation) - anything else fails safe as "no level", never a match.
const normalizeStoredLevel = (level) => {
  const n = Number(level);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
};

// expertise/methods store word levels; map them onto the same 1-5 scale for
// comparison only, one rung per word. Unset/unknown levels are null - never
// a match.
const WORD_LEVEL_ORDINALS = { novice: 1, beginner: 2, intermediate: 3, advanced: 4, expert: 5 };

const normalizeWordLevel = (level) =>
  WORD_LEVEL_ORDINALS[String(level ?? "").trim().toLowerCase()] ?? null;

// Every skill name on the candidate's expertise/methods entries, with its
// normalized level. An expertise entry can carry several names.
const expertiseAndMethodEntries = (candidate) => [
  ...(candidate.expertise || []).flatMap((e) =>
    (e.expertise || []).map((name) => ({ name, level: normalizeWordLevel(e.level) }))
  ),
  ...(candidate.methods || []).map((m) => ({ name: m.group, level: normalizeWordLevel(m.level) })),
];

const sameSkillName = (name, normalizedTerm) =>
  Boolean(name) && String(name).trim().toLowerCase() === normalizedTerm;

// True if any of the candidate's techSkills/qaSkill/expertise/methods entries
// name this chip's skill (case-insensitive) at or above the chip's level
// (floor, not exact). Malformed/out-of-catalog/unset stored levels are
// skipped, not treated as a match.
const candidateMeetsExpertiseChip = (candidate, chip) => {
  const entries = [
    ...(candidate.techSkills || []).map((s) => ({ name: s.language, level: normalizeStoredLevel(s.level) })),
    ...(candidate.qaSkill || []).map((s) => ({ name: s.skills, level: normalizeStoredLevel(s.level) })),
    ...expertiseAndMethodEntries(candidate),
  ];
  return entries.some(
    ({ name, level }) => sameSkillName(name, chip.skillValue) && level !== null && level >= chip.levelOrdinal
  );
};

const roleOptions = [
  { value: "", label: "Select role" },
  ...primaryRoles.map((r) => ({ value: r, label: r })),
];

const TAG_OPTIONS = ["Top Pick", "Strong Fit", "Interesting", "Not a Fit"];

const SELECT_ALL_VALUE = "__select_all__";

// Brand checkbox: orange fill when checked/indeterminate, brown label/box,
// per Figma tokens. Used by row selection and the Main Role filter.
const brandCheckboxSx = {
  color: "var(--Primary-B-500--Main, #463C3B)",
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: "var(--Primary-O-500-Main, #E84610)",
  },
};
const brandLabelColor = "var(--Primary-B-500--Main, #463C3B)";

// Action-column icon buttons render as their own bordered square, not
// borderless floating icons, per Figma.
const actionButtonSx = {
  width: 32,
  height: 32,
  borderRadius: "6px",
  border: "1px solid #DEDFD1",
};

// Search-method tab: underline style with tinted background + bottom accent
// border on the active tab, per Figma Inspect (height 40, radius 8, active
// border-bottom 2px #E84610, active background rgba(232,70,16,0.10)).
// Table header cells: tinted background + matching bottom border, fixed
// height/padding, per Figma Inspect. Labels stay on one line (nowrap), per
// Figma.
const headerCellSx = {
  height: 44,
  padding: "12px 24px",
  whiteSpace: "nowrap",
  fontWeight: 700,
  backgroundColor: (t) => t.palette.background.neutral,
  borderBottom: (t) => `1px solid ${t.palette.background.neutral}`,
};

const searchTabSx = {
  textTransform: "none",
  height: 40,
  padding: "4px 16px",
  borderRadius: "8px !important",
  border: "none",
  borderBottom: "2px solid transparent",
  marginLeft: "0 !important",
  color: "#666666",
  fontWeight: 500,
  gap: 0.75,
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "#E84610",
    backgroundColor: "rgba(232, 70, 16, 0.10)",
    borderBottom: "2px solid #E84610",
  },
};

// Renders the Autocomplete's option list in place instead of in its own
// portaled, positioned popper, so it sits inside the surrounding filter
// popover rather than floating as a second panel. Popper-only props are
// dropped so they don't leak onto the DOM element.
const InlineListPopper = ({ anchorEl, disablePortal, open, ...other }) => <div {...other} />;

// Multi-select dropdown with search + Select All, matching the Autocomplete
// pattern already used elsewhere (e.g. client/src/components/InputControls).
// `showSelectAll`/`brandStyle` default to the original City/Country look;
// Main Role opts into the Figma checkbox-list variant: no Select All, label as
// a caption above a "Search" field with icon, brand checkboxes, and no
// selection summary in the field (the checkboxes and filter chip show it).
// Both variants render their option list inline, so the field and list form
// one dropdown inside the column's filter popover.
const MultiSelectFilter = ({
  label,
  options,
  selected,
  onChange,
  sx,
  showSelectAll = true,
  brandStyle = false,
}) => {
  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const isAllSelected = allValues.length > 0 && selected.length === allValues.length;
  const labelByValue = useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options]
  );

  const handleChange = (_, newValue) => {
    if (newValue.includes(SELECT_ALL_VALUE)) {
      onChange(isAllSelected ? [] : allValues);
      return;
    }
    onChange(newValue);
  };

  const filterOptions = (opts, { inputValue }) => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return opts;
    return opts.filter(
      (v) => v === SELECT_ALL_VALUE || (labelByValue[v] || "").toLowerCase().includes(query)
    );
  };

  const autocomplete = (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      open
      options={showSelectAll ? [SELECT_ALL_VALUE, ...allValues] : allValues}
      value={selected}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={(value) => (value === SELECT_ALL_VALUE ? "Select All" : labelByValue[value] || value)}
      PopperComponent={InlineListPopper}
      slotProps={{ paper: { elevation: 0, sx: { mt: 1, boxShadow: "none" } } }}
      // Autocomplete swallows Escape while its (always-open) list is showing;
      // skip that so Escape reaches the filter popover and closes it.
      onKeyDown={(e) => {
        if (e.key === "Escape") e.defaultMuiPrevented = true;
      }}
      renderOption={(props, value) => (
        <li {...props} key={value} style={brandStyle ? { color: brandLabelColor } : undefined}>
          <Checkbox
            size="small"
            checked={value === SELECT_ALL_VALUE ? isAllSelected : selected.includes(value)}
            indeterminate={value === SELECT_ALL_VALUE && selected.length > 0 && !isAllSelected}
            sx={{ mr: 1, ...(brandStyle ? brandCheckboxSx : {}) }}
          />
          {value === SELECT_ALL_VALUE ? "Select All" : labelByValue[value]}
        </li>
      )}
      renderTags={(value) =>
        brandStyle ? null : (
          <Typography variant="body2" noWrap sx={{ ml: 0.5 }}>
            {value.length === 1 ? labelByValue[value[0]] : `${value.length} selected`}
          </Typography>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={brandStyle ? undefined : label}
          placeholder={brandStyle ? "Search" : "Search…"}
          InputProps={
            brandStyle
              ? {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                        <SearchIcon fontSize="small" sx={{ color: "#999999" }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }
              : params.InputProps
          }
        />
      )}
      sx={sx}
    />
  );

  if (!brandStyle) return autocomplete;
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: brandLabelColor }}>
        {label}
      </Typography>
      {autocomplete}
    </Box>
  );
};

// Plain checkbox list (no search field, no Select All) for short option sets,
// with the same "Filter" caption and brand checkboxes as Main Role's
// MultiSelectFilter variant, per Figma.
const CheckboxListFilter = ({ label, options, selected, onChange }) => {
  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: brandLabelColor }}>
        {label}
      </Typography>
      {options.map((opt) => (
        <MenuItem key={opt.value} onClick={() => toggle(opt.value)} sx={{ px: 1, color: brandLabelColor }}>
          <Checkbox
            size="small"
            checked={selected.includes(opt.value)}
            tabIndex={-1}
            disableRipple
            sx={{ mr: 1, p: 0.5, ...brandCheckboxSx }}
          />
          {opt.label}
        </MenuItem>
      ))}
    </Box>
  );
};

// Results-table columns the "Columns" dropdown can hide. Person's Name (always
// needed to identify the row), the row checkbox and Action are not listed.
const TOGGLEABLE_COLUMNS = [
  { key: "mainRole", label: "Main Role" },
  { key: "seniority", label: "Seniority Level" },
  { key: "city", label: "City" },
  { key: "country", label: "Country" },
  { key: "experience", label: "Industry Experience" },
];
// Hidden (not visible) keys are stored, so a column added later shows by default.
const HIDDEN_COLUMNS_STORAGE_KEY = "nctc-search-hidden-columns";

const loadHiddenColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY));
    return Array.isArray(stored) ? stored.filter((key) => TOGGLEABLE_COLUMNS.some((col) => col.key === key)) : [];
  } catch {
    return [];
  }
};

// "Columns" button + checkbox dropdown; same Popover close behavior
// (click-away, Escape) as ColumnFilterButton. Toggles apply live.
const ColumnsMenuButton = ({ hiddenColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const toggle = (key) =>
    onChange(hiddenColumns.includes(key) ? hiddenColumns.filter((k) => k !== key) : [...hiddenColumns, key]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnOutlinedIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ borderRadius: "6px" }}
      >
        Columns
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ py: 1, minWidth: 220 }}>
          <MenuItem disabled sx={{ px: 1.5, color: brandLabelColor, "&.Mui-disabled": { opacity: 1 } }}>
            <Checkbox size="small" checked disabled tabIndex={-1} sx={{ mr: 1, p: 0.5 }} />
            Person's Name
          </MenuItem>
          {TOGGLEABLE_COLUMNS.map((col) => (
            <MenuItem key={col.key} onClick={() => toggle(col.key)} sx={{ px: 1.5, color: brandLabelColor }}>
              <Checkbox
                size="small"
                checked={!hiddenColumns.includes(col.key)}
                tabIndex={-1}
                disableRipple
                sx={{ mr: 1, p: 0.5, ...brandCheckboxSx }}
              />
              {col.label}
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </>
  );
};

// Column header text doubles as the sort trigger (no separate arrow icon).
// Deliberately shows no sort-state indicator - it renders exactly like plain
// header text; the asc/desc/switch logic lives in AdminSearch's handleSort.
const SortableHeaderLabel = ({ columnKey, onSort, children }) => (
  <ButtonBase
    disableRipple
    onClick={() => onSort(columnKey)}
    sx={{ font: "inherit", color: "inherit", textAlign: "left" }}
  >
    {children}
  </ButtonBase>
);

// Column header's filter icon + popover chrome (selection UI passed as
// children), matching the Figma column-filter pattern. Kept at module scope
// (not nested in AdminSearch) so it isn't redefined - and remounted, dropping
// open popovers - every render.
const ColumnFilterButton = ({ active, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 0.25 }}>
        <FilterAltOutlinedIcon fontSize="small" sx={{ color: active ? "primary.main" : "#999999" }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        // The popover's focus trap focuses its paper on open, beating the
        // field's autoFocus - move focus to a MultiSelectFilter search field,
        // if there is one, once it has opened.
        TransitionProps={{ onEntered: (node) => node.querySelector(".MuiAutocomplete-input")?.focus() }}
      >
        <Box sx={{ p: 2, width: 260 }}>{children}</Box>
      </Popover>
    </>
  );
};

// Expertise Search "Level" button + dropdown panel, per Figma. Own
// rounded-rectangle button (matched height/border with the skills input,
// not merged into it) that hugs its label width; opens a panel below with
// the level options directly, with the current selection shown bold on a full-width highlight band.
const LevelFilterButton = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selectedLabel = EXPERTISE_LEVEL_OPTIONS.find((opt) => opt.value === value)?.label;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          height: 44,
          padding: "0 16px",
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "8px",
          backgroundColor: "#fff",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <Typography sx={{ fontWeight: 400, color: "#1A1A1A", fontSize: 13 }}>
          {selectedLabel || "Select Minimum Skill Level"}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" sx={{ color: "#666666" }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { boxShadow: "none", backgroundColor: "transparent", mt: 0.5 } }}
        // Without disableRestoreFocus, MUI restores focus to this button
        // (the popover's trigger) when it closes - stealing focus away from
        // the skill input right after a level is picked, so a subsequent
        // Enter keypress hits this button (re-opening the dropdown) instead
        // of reaching the skill input. onChange below
        // moves focus back to the skill input explicitly instead; without
        // disableEnforceFocus, the popover's still-active focus trap (it's
        // mid-close, not yet unmounted) immediately pulls focus back off
        // that external target before it can stick.
        disableRestoreFocus
        disableEnforceFocus
      >
        <Box
          sx={{
            width: 240,
            backgroundColor: "#fff",
            borderRadius: "8px",
            border: "1px solid #EEEEEE",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
            py: 0.5,
          }}
        >
          {EXPERTISE_LEVEL_OPTIONS.map((opt) => {
            const selected = opt.value === value;
            return (
              <Box
                key={opt.value}
                onClick={() => {
                  // Clicking the already-selected level clears it, returning
                  // to the no-level broad Candidate.skills match mode.
                  onChange(selected ? "" : opt.value);
                  setAnchorEl(null);
                }}
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: 14,
                  fontWeight: selected ? 700 : 400,
                  color: "#1A1A1A",
                  backgroundColor: selected ? "#F0F0F0" : "transparent",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F7F7F7" },
                }}
              >
                {opt.label}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

// Filter chip: bold field-name prefix + regular value text, outlined (white
// background + light border) rather than filled, per Figma.
const FilterChip = ({ prefix, value, onDelete }) => (
  <Chip
    variant="outlined"
    size="small"
    onDelete={onDelete}
    label={
      <>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {prefix}:
        </Box>{" "}
        {value}
      </>
    }
    sx={{
      borderRadius: "20px",
      borderColor: "#DDDDDD",
      backgroundColor: "#fff",
      "& .MuiChip-deleteIcon": { color: "#999999" },
    }}
  />
);

const formatExperience = (expYears) =>
  expYears == null ? "—" : expYears >= 5 ? "5+ years" : `${expYears} year${expYears !== 1 ? "s" : ""}`;

const getSortValue = (c, column) => {
  const u = c.user || {};
  switch (column) {
    case "name":
      return `${u.firstName || ""} ${u.lastName || ""}`.trim().toLowerCase();
    case "mainRole":
      return getCandidateRole(c).toLowerCase();
    case "seniority":
      return (c.seniority || "").toLowerCase();
    case "city":
      return (u.city || "").toLowerCase();
    case "country":
      return (getCountryLabel(u.country) || "").toLowerCase();
    default:
      return "";
  }
};

// Distinct, trimmed, case-insensitively de-duplicated values as sorted
// options; the first spelling seen wins.
const toDistinctOptions = (values) => {
  const byKey = new Map();
  values.forEach((v) => {
    const value = typeof v === "string" ? v.trim() : "";
    if (value && !byKey.has(value.toLowerCase())) byKey.set(value.toLowerCase(), value);
  });
  return Array.from(byKey.values())
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
};

const filterCandidates = (candidates, filters) => {
  let list = [...candidates];
  const {
    keywordAst,
    personName,
    mainRoles,
    seniorityLevels,
    countries,
    cities,
    expertiseChips,
    broadSkillsQuery,
  } = filters;

  if (personName?.trim()) {
    const q = personName.trim().toLowerCase();
    list = list.filter((c) => {
      const fn = (c.user?.firstName || "").toLowerCase();
      const ln = (c.user?.lastName || "").toLowerCase();
      return fn.includes(q) || ln.includes(q) || `${fn} ${ln}`.includes(q) || `${ln} ${fn}`.includes(q);
    });
  }
  if (mainRoles && mainRoles.length > 0) {
    list = list.filter(
      (c) =>
        mainRoles.includes(getCandidateRole(c)) ||
        (Array.isArray(c.secondaryRoles) && c.secondaryRoles.some((r) => mainRoles.includes(r)))
    );
  }
  if (seniorityLevels && seniorityLevels.length > 0) {
    list = list.filter((c) => seniorityLevels.includes(c.seniority));
  }
  if (countries && countries.length > 0) {
    list = list.filter((c) =>
      // country may be stored as a JSON string (onboarding) or a plain
      // name - match on its label, same as the Country column shows.
      countries.some((val) => getCountryLabel(c.user?.country).trim().toLowerCase() === val.toLowerCase())
    );
  }
  if (cities && cities.length > 0) {
    list = list.filter((c) =>
      cities.some((val) => (c.user?.city || "").trim().toLowerCase() === val.toLowerCase())
    );
  }
  if (keywordAst) {
    list = list.filter((c) => evaluateBooleanQuery(keywordAst, c));
  }
  // Broad, name-only match - live while text sits in the skill input (see
  // handleLevelSelected/broadSkillsQuery at the call site). Substring on
  // Candidate.skills; exact (case-insensitive) on the freeform CV
  // expertise/methods names, so "java" doesn't hit "JavaScript". Levels are
  // ignored here. Independent of, and combines via AND with, expertiseChips.
  const skillTerms = parseSkillTerms(broadSkillsQuery);
  if (skillTerms.length > 0) {
    list = list.filter((c) => {
      const skillLabels = (c.skills || []).map((s) => String(s).toLowerCase());
      const cvEntries = expertiseAndMethodEntries(c);
      return skillTerms.some(
        (term) =>
          skillLabels.some((s) => s.includes(term)) ||
          cvEntries.some(({ name }) => sameSkillName(name, term))
      );
    });
  }
  if (expertiseChips && expertiseChips.length > 0) {
    list = list.filter((c) =>
      expertiseChips.every((chip) => candidateMeetsExpertiseChip(c, chip))
    );
  }
  return list;
};

const AdminSearch = () => {
  const [searchTab, setSearchTab] = useState("keyword");

  const [generalCvContent, setGeneralCvContent] = useState("");
  const [personName, setPersonName] = useState("");
  const [mainRoles, setMainRoles] = useState([]);
  const [seniorityFilter, setSeniorityFilter] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [skills, setSkills] = useState("");
  const [expertiseChips, setExpertiseChips] = useState([]);
  const [skillEntryError, setSkillEntryError] = useState("");
  const skillInputRef = useRef(null);
  const [allCandidates, setAllCandidates] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({ column: null, direction: "asc" });
  const [hiddenColumns, setHiddenColumns] = useState(loadHiddenColumns);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [previewCandidate, setPreviewCandidate] = useState(null);
  const [menuState, setMenuState] = useState({ anchorEl: null, candidate: null });
  const [cvMessage, setCvMessage] = useState("");

  const [tagDialogCandidateId, setTagDialogCandidateId] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState("");
  const [opportunities, setOpportunities] = useState([]);
  const [savingTag, setSavingTag] = useState(false);

  const openTagDialog = async (candidateId) => {
    setTagDialogCandidateId(candidateId);
    setSelectedTag(null);
    setSelectedOpportunityId("");
    if (opportunities.length === 0) {
      try {
        const { data } = await api.get("/opportunities");
        setOpportunities(data || []);
      } catch (err) {
        console.error("Failed to load opportunities:", err);
      }
    }
  };

  const closeTagDialog = () => {
    setTagDialogCandidateId(null);
    setSelectedTag(null);
    setSelectedOpportunityId("");
  };

  const handleSaveTag = async () => {
    if (!selectedTag || !selectedOpportunityId) return;
    try {
      setSavingTag(true);
      const { data } = await api.post(`/candidates/${tagDialogCandidateId}/tags`, {
        tag: selectedTag,
        opportunity: selectedOpportunityId,
      });
      setResults((prev) =>
        prev.map((c) => (c._id === tagDialogCandidateId ? data : c))
      );
      closeTagDialog();
    } catch (err) {
      console.error("Failed to save tag:", err);
    } finally {
      setSavingTag(false);
    }
  };

  const handleDownloadCV = async (candidateId) => {
    try {
      const { data } = await api.get(`/candidates/${candidateId}/cv`);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setCvMessage("No CV available");
      } else {
        setCvMessage(err.response?.data?.error || err.message || "Failed to fetch CV");
      }
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/candidates/");
      const list = data || [];
      setAllCandidates(list);
      setResults(list);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to fetch candidates");
      setAllCandidates([]);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePersonNameChange = async (e) => {
    const value = e.target.value;
    setPersonName(value);
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setResults(allCandidates);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const searchParams = new URLSearchParams({ name: trimmed });
      const { data } = await api.get(`/candidates/?${searchParams}`);
      setResults(data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to fetch candidates");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Same column toggles A-Z <-> Z-A; a different column starts over at A-Z.
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      column: columnKey,
      direction: prev.column === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(hiddenColumns));
    } catch {
      // Storage unavailable (e.g. blocked site data): the selection just won't persist.
    }
  }, [hiddenColumns]);

  const isColumnVisible = (key) => !hiddenColumns.includes(key);

  // Hiding a column also clears its filter, so nothing narrows the results
  // without a visible funnel; re-showing it starts unfiltered.
  const columnFilterClearers = {
    mainRole: () => setMainRoles([]),
    seniority: () => setSeniorityFilter([]),
    city: () => setCityFilter([]),
    country: () => setCountries([]),
  };
  const handleHiddenColumnsChange = (next) => {
    next.filter((key) => !hiddenColumns.includes(key)).forEach((key) => columnFilterClearers[key]?.());
    setHiddenColumns(next);
  };

  // Selecting a level is the single action that confirms the typed skill as
  // a chip and resets the inputs for the next entry (the level itself is
  // never held in state). Re-entering an already-chipped skill replaces its
  // level rather than duplicating it. Does nothing when the skill input is
  // empty (nothing to attach the level to); when the text isn't a catalog
  // skill it shows an error and keeps the text, staying in the companion
  // broad-match mode (see broadSkillsQuery).
  const handleLevelSelected = (selectedLevel) => {
    if (!selectedLevel || !skills.trim()) return;
    const match = findExpertiseSkill(skills);
    if (!match) {
      setSkillEntryError("No matching skill found");
      return;
    }
    const skillValue = match.value.toLowerCase();
    const levelLabel = EXPERTISE_LEVEL_OPTIONS.find((o) => o.value === selectedLevel)?.label;
    const levelOrdinal = expertiseLevelOrdinal(selectedLevel);
    setExpertiseChips((prev) => [
      ...prev.filter((chip) => chip.skillValue !== skillValue),
      { skillValue, skillLabel: match.name, levelOrdinal, levelLabel },
    ]);
    setSkills("");
    setSkillEntryError("");
  };

  const removeExpertiseChip = (skillValue) => {
    setExpertiseChips((prev) => prev.filter((chip) => chip.skillValue !== skillValue));
  };

  const clearExpertiseChips = () => setExpertiseChips([]);

  const keywordQuery = useMemo(() => {
    if (generalCvContent.trim().length < SKILL_MIN_CHARS) return { ast: null, error: null };
    return parseBooleanQuery(generalCvContent);
  }, [generalCvContent]);

  // Every active filter except the four column checkbox filters (Main Role,
  // Seniority, City, Country).
  const baseResults = useMemo(
    () =>
      filterCandidates(results, {
        expertiseChips,
        // Broad Candidate.skills match: live-filters as the user types until
        // a level is selected, which turns the text into a chip and clears it
        // (see handleLevelSelected).
        broadSkillsQuery: skills,
        keywordAst: keywordQuery.ast,
      }),
    [results, expertiseChips, skills, keywordQuery.ast]
  );

  // Each column filter builds its options from the results of every filter
  // but its own (so checking one value doesn't hide the others); the table
  // applies all of them. Filters AND together, so applying them in stages
  // gives the same result as applying them all at once.
  const columnFilteredResults = useMemo(() => {
    const columnFilters = { mainRoles, seniorityLevels: seniorityFilter, countries, cities: cityFilter };
    const except = (key) => filterCandidates(baseResults, { ...columnFilters, [key]: [] });
    return {
      all: filterCandidates(baseResults, columnFilters),
      exceptMainRole: except("mainRoles"),
      exceptSeniority: except("seniorityLevels"),
      exceptCountry: except("countries"),
      exceptCity: except("cities"),
    };
  }, [baseResults, mainRoles, seniorityFilter, countries, cityFilter]);

  const visibleResults = columnFilteredResults.all;
  const resultsExceptMainRole = columnFilteredResults.exceptMainRole;
  const resultsExceptSeniority = columnFilteredResults.exceptSeniority;

  // Main + secondary roles present after the other filters, plus anything
  // already checked so it can always be unchecked.
  const mainRoleOptions = useMemo(() => {
    const seen = new Set(mainRoles);
    resultsExceptMainRole.forEach((c) => {
      const role = getCandidateRole(c);
      if (role) seen.add(role);
      (Array.isArray(c.secondaryRoles) ? c.secondaryRoles : []).forEach((r) => r && seen.add(r));
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((role) => ({ value: role, label: role }));
  }, [resultsExceptMainRole, mainRoles]);

  // Seniority values present after the other filters, plus anything already
  // checked. Known levels keep their config (junior -> senior) order; any
  // other stored value follows alphabetically.
  const seniorityFilterOptions = useMemo(() => {
    const seen = new Set(seniorityFilter);
    resultsExceptSeniority.forEach((c) => c.seniority && seen.add(c.seniority));
    const rank = (v) => {
      const i = seniorityLevels.indexOf(v);
      return i === -1 ? seniorityLevels.length : i;
    };
    return Array.from(seen)
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .map((level) => ({ value: level, label: level }));
  }, [resultsExceptSeniority, seniorityFilter]);

  // Countries (by label - see filterCandidates) and cities present after the
  // other filters, plus anything already checked. Free-text variants that
  // differ only by case/whitespace collapse into one option, matching the
  // filter's case-insensitive comparison; checked values keep their spelling.
  const countryFilterOptions = useMemo(
    () =>
      toDistinctOptions([
        ...countries,
        ...columnFilteredResults.exceptCountry.map((c) => getCountryLabel(c.user?.country)),
      ]),
    [columnFilteredResults.exceptCountry, countries]
  );

  const cityFilterOptions = useMemo(
    () => toDistinctOptions([...cityFilter, ...columnFilteredResults.exceptCity.map((c) => c.user?.city)]),
    [columnFilteredResults.exceptCity, cityFilter]
  );

  const sortedResults = useMemo(() => {
    if (!sortConfig.column) return visibleResults;
    const list = [...visibleResults];
    list.sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.column);
      const bVal = getSortValue(b, sortConfig.column);
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [visibleResults, sortConfig]);

  useEffect(() => {
    setPage(0);
  }, [visibleResults]);

  const paginatedResults = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedResults.slice(start, start + rowsPerPage);
  }, [sortedResults, page, rowsPerPage]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageIds = paginatedResults.map((c) => c._id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const displayCount = visibleResults.length;

  const tagDialogCandidate = results.find((c) => c._id === tagDialogCandidateId);
  const tagDialogCandidateName =
    [tagDialogCandidate?.user?.firstName, tagDialogCandidate?.user?.lastName].filter(Boolean).join(" ") ||
    "candidate";

  const hasActiveChips =
    personName.trim().length >= 3 ||
    mainRoles.length > 0 ||
    seniorityFilter.length > 0 ||
    countries.length > 0 ||
    cityFilter.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
          p: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Typography variant="xl">How would you like to search?</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            sx={{ borderRadius: "7px" }}
          >
            EXPORT
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Choose a search method
        </Typography>

        <ToggleButtonGroup
          value={searchTab}
          exclusive
          onChange={(_, v) => v && setSearchTab(v)}
          sx={{ mb: 3, gap: 0.5 }}
        >
          <ToggleButton value="keyword" sx={searchTabSx}>
            <SearchIcon fontSize="small" /> Keyword Search
          </ToggleButton>
          <ToggleButton value="expertise" sx={searchTabSx}>
            <WorkOutlineIcon fontSize="small" /> Expertise Search
          </ToggleButton>
        </ToggleButtonGroup>

        {searchTab === "keyword" ? (
          <Box sx={{ mb: 3 }}>
            <TextField
              id="keyword-search-query"
              name="keyword-search-query"
              // Nonstandard token: Chrome ignores "off" for its phone/address autofill heuristics
              autoComplete="keyword-search"
              size="small"
              fullWidth
              placeholder='e.g. "senior developer" AND (react OR vue)'
              value={generalCvContent}
              onChange={(e) => setGeneralCvContent(e.target.value)}
              error={Boolean(keywordQuery.error)}
              helperText={keywordQuery.error || ""}
              sx={{ "& .MuiOutlinedInput-root": { height: 44 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#999999" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#1A1A1A",
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 16 }} /> Search Tips
              </Link>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                id="expertise-search-query"
                name="expertise-search-query"
                // Nonstandard token: Chrome ignores "off" for its phone/address autofill heuristics
                autoComplete="expertise-search"
                size="small"
                placeholder="Search skills, methods or tools"
                value={skills}
                onChange={(e) => {
                  setSkills(e.target.value);
                  setSkillEntryError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                error={Boolean(skillEntryError)}
                helperText={skillEntryError || ""}
                inputRef={skillInputRef}
                sx={{ flex: "0 0 75%", "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999999" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <LevelFilterButton
                value=""
                onChange={(newLevel) => {
                  handleLevelSelected(newLevel);
                  // Selecting a level closes the popover, which would
                  // otherwise leave focus on the Level button itself (see
                  // disableRestoreFocus above) - return it to the skill
                  // input so the next skill can be typed straight away.
                  skillInputRef.current?.focus();
                }}
              />
            </Box>
            {expertiseChips.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 2 }}>
                {expertiseChips.map((chip) => (
                  <Chip
                    key={chip.skillValue}
                    size="small"
                    label={`${chip.skillLabel}, ${chip.levelLabel}`}
                    onDelete={() => removeExpertiseChip(chip.skillValue)}
                    sx={{ borderRadius: "20px" }}
                  />
                ))}
                <Link component="button" type="button" variant="body2" onClick={clearExpertiseChips}>
                  Clear All
                </Link>
              </Box>
            )}
          </Box>
        )}
        {searchTab === "expertise" && <Divider sx={{ borderColor: "#EEEEEE", mb: 3 }} />}

        {hasActiveChips && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            {personName.trim().length >= 3 && (
              <FilterChip
                prefix="Person's Name"
                value={personName.trim()}
                onDelete={() => handlePersonNameChange({ target: { value: "" } })}
              />
            )}
            {mainRoles.length > 0 && (
              <FilterChip
                prefix="Main Role"
                value={mainRoles
                  .map((v) => roleOptions.find((o) => o.value === v)?.label || v)
                  .join(", ")}
                onDelete={() => setMainRoles([])}
              />
            )}
            {seniorityFilter.length > 0 && (
              <FilterChip
                prefix="Seniority Level"
                value={seniorityFilter.join(", ")}
                onDelete={() => setSeniorityFilter([])}
              />
            )}
            {countries.length > 0 && (
              <FilterChip
                prefix="Country"
                value={countries.join(", ")}
                onDelete={() => setCountries([])}
              />
            )}
            {cityFilter.length > 0 && (
              <FilterChip
                prefix="City"
                value={cityFilter.join(", ")}
                onDelete={() => setCityFilter([])}
              />
            )}
          </Box>
        )}

        {searchTab !== "expertise" && <Divider sx={{ mb: 3 }} />}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="xl" sx={{ fontWeight: 700 }}>
                  Search Results ({displayCount})
                </Typography>
                <Typography variant="body2">{displayCount} results found</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <ColumnsMenuButton hiddenColumns={hiddenColumns} onChange={handleHiddenColumnsChange} />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={selectedIds.size === 0}
                  startIcon={<EmailOutlinedIcon />}
                  sx={{
                    borderRadius: "6px",
                    color: "#616161",
                    borderColor: "#616161",
                    "&:hover": { borderColor: "#424242", color: "#424242" },
                    "&.Mui-disabled": { color: "#AAAAAA", borderColor: "#CCCCCC" },
                  }}
                >
                  SEND BULK EMAIL{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={headerCellSx}>
                      <Checkbox
                        size="small"
                        sx={brandCheckboxSx}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                      />
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <SortableHeaderLabel columnKey="name" onSort={handleSort}>
                          Person's Name
                        </SortableHeaderLabel>
                        <ColumnFilterButton active={personName.trim().length >= 3}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Search by name"
                            placeholder="Search by name…"
                            value={personName}
                            onChange={handlePersonNameChange}
                          />
                        </ColumnFilterButton>
                      </Box>
                    </TableCell>
                    {isColumnVisible("mainRole") && (
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <SortableHeaderLabel columnKey="mainRole" onSort={handleSort}>
                            Main Role
                          </SortableHeaderLabel>
                          <ColumnFilterButton active={mainRoles.length > 0}>
                            <MultiSelectFilter
                              label="Filter"
                              options={mainRoleOptions}
                              selected={mainRoles}
                              onChange={setMainRoles}
                              showSelectAll={false}
                              brandStyle
                              sx={{ minWidth: 220 }}
                            />
                          </ColumnFilterButton>
                        </Box>
                      </TableCell>
                    )}
                    {isColumnVisible("seniority") && (
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <SortableHeaderLabel columnKey="seniority" onSort={handleSort}>
                            Seniority Level
                          </SortableHeaderLabel>
                          <ColumnFilterButton active={seniorityFilter.length > 0}>
                            <CheckboxListFilter
                              label="Filter"
                              options={seniorityFilterOptions}
                              selected={seniorityFilter}
                              onChange={setSeniorityFilter}
                            />
                          </ColumnFilterButton>
                        </Box>
                      </TableCell>
                    )}
                    {isColumnVisible("city") && (
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <SortableHeaderLabel columnKey="city" onSort={handleSort}>
                            City
                          </SortableHeaderLabel>
                          <ColumnFilterButton active={cityFilter.length > 0}>
                            <MultiSelectFilter
                              label="Filter"
                              options={cityFilterOptions}
                              selected={cityFilter}
                              onChange={setCityFilter}
                              showSelectAll={false}
                              brandStyle
                              sx={{ minWidth: 220 }}
                            />
                          </ColumnFilterButton>
                        </Box>
                      </TableCell>
                    )}
                    {isColumnVisible("country") && (
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <SortableHeaderLabel columnKey="country" onSort={handleSort}>
                            Country
                          </SortableHeaderLabel>
                          <ColumnFilterButton active={countries.length > 0}>
                            <MultiSelectFilter
                              label="Filter"
                              options={countryFilterOptions}
                              selected={countries}
                              onChange={setCountries}
                              showSelectAll={false}
                              brandStyle
                              sx={{ minWidth: 220 }}
                            />
                          </ColumnFilterButton>
                        </Box>
                      </TableCell>
                    )}
                    {isColumnVisible("experience") && <TableCell sx={headerCellSx}>Industry Experience</TableCell>}
                    <TableCell align="right" sx={headerCellSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedResults.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3 + TOGGLEABLE_COLUMNS.length - hiddenColumns.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No candidates found
                      </TableCell>
                    </TableRow>
                  )}
                  {paginatedResults.map((c) => {
                    const u = c.user || {};
                    const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || "Unknown";

                    return (
                      <TableRow
                        key={c._id}
                        hover
                        selected={selectedIds.has(c._id)}
                        sx={{
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: (t) => t.palette.background.subtle,
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            sx={brandCheckboxSx}
                            checked={selectedIds.has(c._id)}
                            onChange={() => toggleSelect(c._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography component="div" fontWeight={600}>
                            {name}
                          </Typography>
                          {(c.tags || []).length > 0 && (
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                              {c.tags.map((t, i) => (
                                <Chip key={t._id || i} label={t.tag} size="small" />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        {isColumnVisible("mainRole") && <TableCell>{getCandidateRole(c) || "—"}</TableCell>}
                        {isColumnVisible("seniority") && <TableCell>{c.seniority || "—"}</TableCell>}
                        {isColumnVisible("city") && <TableCell>{u.city || "—"}</TableCell>}
                        {isColumnVisible("country") && <TableCell>{getCountryLabel(u.country) || "—"}</TableCell>}
                        {isColumnVisible("experience") && <TableCell>{formatExperience(c.experienceYears)}</TableCell>}
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              aria-label="View candidate details"
                              onClick={() => setPreviewCandidate(c)}
                              sx={actionButtonSx}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => setMenuState({ anchorEl: e.currentTarget, candidate: c })}
                              sx={actionButtonSx}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedResults.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                color: "#666666",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                },
                "& .MuiTablePagination-select": { fontSize: "13px" },
                "& .MuiTablePagination-actions button": {
                  border: "1px solid #DEDFD1",
                  borderRadius: "6px",
                  mx: 0.25,
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={() => setMenuState({ anchorEl: null, candidate: null })}
      >
        <MenuItem
          onClick={() => {
            const candidate = menuState.candidate;
            setMenuState({ anchorEl: null, candidate: null });
            if (candidate) openTagDialog(candidate._id);
          }}
        >
          <LocalOfferOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Add Tags
        </MenuItem>
        <MenuItem
          onClick={() => {
            const candidate = menuState.candidate;
            setMenuState({ anchorEl: null, candidate: null });
            if (candidate) handleDownloadCV(candidate._id);
          }}
        >
          <DownloadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Request CV
        </MenuItem>
      </Menu>

      <CandidateQuickViewDialog candidate={previewCandidate} onClose={() => setPreviewCandidate(null)} />

      <Dialog
        open={Boolean(tagDialogCandidateId)}
        onClose={closeTagDialog}
        PaperProps={{
          sx: {
            width: 575,
            maxWidth: "calc(100% - 64px)",
            borderRadius: "8px",
            backgroundColor: "#FFF",
            boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", alignSelf: "stretch" }}>
            <Box>
              <Typography
                sx={{
                  color: "#000",
                  fontFamily: '"Readex Pro", sans-serif',
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: "30px",
                }}
              >
                Add Tags
              </Typography>
              <Typography
                sx={{
                  color: "#000",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: 300,
                  lineHeight: "24px",
                }}
              >
                Add a tag to {tagDialogCandidateName}
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeTagDialog} sx={{ mt: -0.5, mr: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignSelf: "stretch" }}>
            {TAG_OPTIONS.map((option) => {
              const selected = selectedTag === option;
              return (
                <ToggleButton
                  key={option}
                  value={option}
                  selected={selected}
                  onClick={() => setSelectedTag(option)}
                  sx={{
                    height: 48,
                    px: "22px",
                    textTransform: "none",
                    borderRadius: "8px !important",
                    border: "1px solid #DDDDDD",
                    backgroundColor: "#FFF",
                    color: "#000",
                    "&.Mui-selected, &.Mui-selected:hover": {
                      border: "1px solid #463C3B",
                      backgroundColor: "#EAE7E6",
                      color: "#000",
                      boxShadow:
                        "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
                    },
                  }}
                >
                  {option}
                </ToggleButton>
              );
            })}
          </Box>

          {selectedTag && (
            <FormControl fullWidth sx={{ alignSelf: "stretch" }}>
              <InputLabel id="tag-opportunity-label" shrink>Opportunity *</InputLabel>
              <Select
                labelId="tag-opportunity-label"
                label="Opportunity *"
                displayEmpty
                notched
                value={selectedOpportunityId}
                onChange={(e) => setSelectedOpportunityId(e.target.value)}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Box component="span" sx={{ color: "#999999" }}>
                        Select Opportunity to add tag to
                      </Box>
                    );
                  }
                  const opp = opportunities.find((o) => o._id === value);
                  return opp ? `${opp.name}${opp.client?.name ? ` — ${opp.client.name}` : ""}` : value;
                }}
              >
                {opportunities.map((o) => (
                  <MenuItem key={o._id} value={o._id}>
                    {o.name}{o.client?.name ? ` — ${o.client.name}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, alignSelf: "stretch" }}>
            <Button
              onClick={closeTagDialog}
              sx={{
                px: "22px",
                py: 1,
                borderRadius: "8px",
                border: "1px solid #463C3B",
                color: "#463C3B",
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              disabled={!selectedTag || !selectedOpportunityId || savingTag}
              onClick={handleSaveTag}
              sx={{
                px: "22px",
                py: 1,
                borderRadius: "8px",
                backgroundColor: "#E84610",
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
                "&:hover": { backgroundColor: "#C94030" },
                "&.Mui-disabled": { backgroundColor: "#F5B8A6", color: "#fff" },
              }}
            >
              SAVE
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={Boolean(cvMessage)}
        autoHideDuration={4000}
        onClose={() => setCvMessage("")}
        message={cvMessage}
      />
    </Box>
  );
};

export default AdminSearch;
